import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import ProposalsTab from '../components/ProposalsTab';
import CertSignaturePanel from '../components/CertSignaturePanel';
import ModuleKanbanMenu from '../components/ModuleKanbanMenu';
import {
  LogOut,
  FileText,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  Building2,
  Mail,
  Phone,
  Calendar,
  Plus,
  Eye,
  Copy,
  Link,
  FileCheck,
  Upload,
  Download,
  X,
  Loader2,
  Receipt,
  User,
  MapPin,
  Globe,
  Briefcase,
  Trash2,
  PenTool,
  Save,
  Edit3,
  Users,
  ShieldCheck,
  BookOpen,
  Wallet
} from 'lucide-react';

import { generateBrochure } from '../utils/brochurePdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContratosPage = () => {
  const navigate = useNavigate();
  const invoiceInputRef = useRef(null);
  const signatureRef = useRef(null);
  
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  // Modo de firma para Analytica: 'draw' | 'upload' | 'cert'
  const [signMode, setSignMode] = useState('draw');
  const [uploadedSig, setUploadedSig] = useState(null);
  const [certData, setCertData] = useState(null);
  const sigFileRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [filter, setFilter] = useState('all');
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState('contracts');  // 'proposals' | 'contracts'
  const [currentStep, setCurrentStep] = useState(1);
  const [editingContractId, setEditingContractId] = useState(null);
  const [isUploadingCustom, setIsUploadingCustom] = useState(false);
  const customContractInputRef = useRef(null);
  
  // Form data for new contract
  const [formData, setFormData] = useState({
    // Client data
    client_company_name: '',
    client_representative_name: '',
    client_representative_title: 'Sr.',
    client_ruc: '',
    client_address: '',
    client_city: '',
    client_country: 'Ecuador',
    client_email: '',
    client_phone: '',
    // Software data
    software_name: '',
    software_description: '',
    modules: [],
    // Schedule
    total_weeks: 8,
    schedule_activities: [],
    // Payments
    payment_phases: [],
    annual_costs: [],
    total_price: 0
  });

  // Temp inputs for adding items
  const [newActivity, setNewActivity] = useState({ activity: '', weeks: [] });
  const [newPayment, setNewPayment] = useState({ description: '', amount: '', week: 1 });
  const [newAnnualCost, setNewAnnualCost] = useState({ activity: '', value: '', time_period: 'Anual' });
  const [newModule, setNewModule] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchContracts();
  }, [navigate]);

  const fetchContracts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Error al cargar los contratos');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_company_name: '',
      client_representative_name: '',
      client_representative_title: 'Sr.',
      client_ruc: '',
      client_address: '',
      client_city: '',
      client_country: 'Ecuador',
      client_email: '',
      client_phone: '',
      software_name: '',
      software_description: '',
      modules: [],
      total_weeks: 8,
      schedule_activities: [],
      payment_phases: [],
      annual_costs: [],
      total_price: 0
    });
    setCurrentStep(1);
  };

  const fieldLabels = {
    client_company_name: 'Nombre de la Compañía',
    client_representative_name: 'Nombre del Representante',
    client_representative_title: 'Título',
    client_ruc: 'RUC / Identificación',
    client_address: 'Dirección',
    client_city: 'Ciudad',
    client_country: 'País',
    client_email: 'Email',
    client_phone: 'Teléfono',
    software_name: 'Nombre del Software',
    total_weeks: 'Semanas',
    total_price: 'Precio Total'
  };

  const parseApiError = (error) => {
    const detail = error.response?.data?.detail;
    if (!detail) return 'Error en la operación';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => {
        if (typeof e === 'object') {
          const field = e.loc?.[e.loc.length - 1];
          const label = fieldLabels[field] || field || '';
          return label ? `${label}: ${e.msg}` : (e.msg || JSON.stringify(e));
        }
        return String(e);
      }).join('; ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: {
        if (!formData.client_company_name || formData.client_company_name.length < 2) {
          toast.error('Nombre de la Compañía debe tener al menos 2 caracteres');
          return false;
        }
        if (!formData.client_representative_name || formData.client_representative_name.length < 2) {
          toast.error('Nombre del Representante debe tener al menos 2 caracteres');
          return false;
        }
        if (!formData.client_ruc || formData.client_ruc.length < 10) {
          toast.error('RUC / Identificación debe tener al menos 10 caracteres');
          return false;
        }
        if (!formData.client_email || !formData.client_email.includes('@')) {
          toast.error('Ingrese un email válido');
          return false;
        }
        if (!formData.client_phone || formData.client_phone.length < 7) {
          toast.error('Teléfono debe tener al menos 7 caracteres');
          return false;
        }
        if (!formData.client_address || formData.client_address.length < 5) {
          toast.error('Dirección debe tener al menos 5 caracteres');
          return false;
        }
        if (!formData.client_city || formData.client_city.length < 2) {
          toast.error('Ciudad debe tener al menos 2 caracteres');
          return false;
        }
        return true;
      }
      case 2: {
        if (!formData.software_name || formData.software_name.length < 2) {
          toast.error('Nombre del Software debe tener al menos 2 caracteres');
          return false;
        }
        return true;
      }
      case 3:
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleCreateContract = async () => {
    // Calculate total price from payment phases
    const totalPrice = formData.payment_phases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    try {
      const payload = {
        ...formData,
        total_price: totalPrice || parseFloat(formData.total_price) || 1000
      };
      
      if (editingContractId) {
        await axios.put(`${API}/contracts/${editingContractId}`, payload);
        toast.success('Contrato actualizado exitosamente');
      } else {
        await axios.post(`${API}/contracts`, payload);
        toast.success('Contrato creado exitosamente (Borrador)');
      }
      setShowCreateModal(false);
      setEditingContractId(null);
      resetForm();
      fetchContracts();
    } catch (error) {
      console.error('Error saving contract:', error);
      toast.error(parseApiError(error));
    }
  };

  const handleEditContract = (contract) => {
    setFormData({
      client_company_name: contract.client_company_name || '',
      client_representative_name: contract.client_representative_name || '',
      client_representative_title: contract.client_representative_title || 'Sr.',
      client_ruc: contract.client_ruc || '',
      client_address: contract.client_address || '',
      client_city: contract.client_city || '',
      client_country: contract.client_country || 'Ecuador',
      client_email: contract.client_email || '',
      client_phone: contract.client_phone || '',
      software_name: contract.software_name || '',
      software_description: contract.software_description || '',
      modules: contract.modules || [],
      total_weeks: contract.total_weeks || 8,
      schedule_activities: contract.schedule_activities || [],
      payment_phases: contract.payment_phases || [],
      annual_costs: contract.annual_costs || [],
      total_price: contract.total_price || 0
    });
    setEditingContractId(contract.id);
    setCurrentStep(1);
    setShowDetailModal(false);
    setShowCreateModal(true);
  };

  const handleSendContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/send`);
      toast.success('Contrato enviado al cliente. Se ha enviado un email con el link de firma.');
      fetchContracts();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error sending contract:', error);
      toast.error(parseApiError(error));
    }
  };

  const handleSignByAnalytica = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Por favor firme el documento');
      return;
    }

    setIsSigning(true);
    try {
      const signatureImage = signatureRef.current.toDataURL('image/png');
      
      await axios.post(`${API}/contracts/${selectedContract.id}/sign-analytica`, {
        signature_image: signatureImage,
        signer_name: user?.username || 'Gerencia Analytica'
      });
      
      toast.success('Contrato firmado y completado exitosamente. Se ha notificado al cliente.');
      setShowSignModal(false);
      setShowDetailModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error(parseApiError(error));
    } finally {
      setIsSigning(false);
    }
  };

  const handleSignAnalyticaWithImage = async (dataUrl) => {
    setIsSigning(true);
    try {
      await axios.post(`${API}/contracts/${selectedContract.id}/sign-analytica`, {
        signature_image: dataUrl,
        signer_name: user?.username || 'Gerencia Analytica'
      });
      toast.success('Contrato firmado con imagen y completado exitosamente.');
      setShowSignModal(false);
      setShowDetailModal(false);
      fetchContracts();
    } catch (e) {
      toast.error(parseApiError(e));
    } finally { setIsSigning(false); }
  };

  const handleSignAnalyticaWithCert = async (p12_base64, password) => {
    setIsSigning(true);
    try {
      // Fetch contract PDF, convert to base64
      const r1 = await axios.get(`${API}/contracts/${selectedContract.id}/download-pdf`, { responseType: 'blob' });
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          resolve(result.includes(',') ? result.split(',', 2)[1] : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(r1.data);
      });
      const r = await axios.post(`${API}/signatures/sign-as-analytica`, {
        p12_base64, password,
        target_type: 'contract',
        target_id: selectedContract.id,
        pdf_base64: pdfBase64,
      });
      toast.success(`Contrato firmado con certificado PAdES · ${r.data.cert_info?.common_name}`);
      setShowSignModal(false);
      setShowDetailModal(false);
      fetchContracts();
    } catch (e) {
      toast.error(parseApiError(e));
    } finally { setIsSigning(false); }
  };

  const handleUploadInvoice = async (e, contractId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10MB');
      return;
    }

    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const response = await axios.post(`${API}/contracts/${contractId}/upload-invoice`, {
            invoice_data: event.target.result
          });
          toast.success('Factura subida exitosamente');
          setSelectedContract(response.data.contract);
          fetchContracts();
        } catch (error) {
          console.error('Error uploading invoice:', error);
          toast.error('Error al subir la factura');
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Error al leer el archivo');
      setIsUploading(false);
    }
  };

  const handleUploadCustomContract = async (e, contractId) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Solo se acepta archivo .docx');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar 5MB');
      return;
    }
    setIsUploadingCustom(true);
    const tid = toast.loading('Subiendo y corrigiendo ortografia...');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(
        `${API}/contracts/${contractId}/upload-custom-contract`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
      );
      toast.dismiss(tid);
      const spell = response.data?.spell_checked;
      toast.success(
        spell
          ? `Contrato personalizado cargado y ortografia corregida (${response.data.sections_count} secciones)`
          : `Contrato personalizado cargado (${response.data.sections_count} secciones). La correccion ortografica no esta disponible ahora.`
      );
      const updated = await axios.get(`${API}/contracts/${contractId}`);
      setSelectedContract(updated.data);
      fetchContracts();
    } catch (error) {
      toast.dismiss(tid);
      console.error('Error uploading custom contract:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el contrato personalizado');
    } finally {
      setIsUploadingCustom(false);
    }
  };

  const handleRemoveCustomContract = async (contractId) => {
    if (!window.confirm('Quitar el contrato personalizado y volver al template estandar?')) return;
    try {
      await axios.delete(`${API}/contracts/${contractId}/custom-contract`);
      toast.success('Template estandar restaurado');
      const updated = await axios.get(`${API}/contracts/${contractId}`);
      setSelectedContract(updated.data);
      fetchContracts();
    } catch (error) {
      toast.error('Error al quitar el contrato personalizado');
    }
  };

  const handleDownloadPDF = async (contractId) => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/download-pdf`, {
        responseType: 'blob'
      });
      
      const contract = contracts.find(c => c.id === contractId);
      const isDraft = contract?.status !== 'completed';
      const filename = isDraft 
        ? `contrato_${contract?.contract_number || contractId}_BORRADOR.pdf`
        : `contrato_${contract?.contract_number || contractId}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteContract = async (contract) => {
    const id = typeof contract === 'string' ? contract : contract.id;
    const status = typeof contract === 'string' ? null : contract.status;
    const force = status && status !== 'draft';
    const msg = force
      ? `Va a eliminar un contrato en estado "${status}". Esta accion NO se puede deshacer. ¿Continuar?`
      : 'Esta seguro de eliminar este contrato borrador?';
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.delete(`${API}/contracts/${id}`, { params: { force: force ? true : undefined } });
      if (res.data?.proposal_unlocked) {
        toast.success('Contrato eliminado. La propuesta queda lista para regenerar el contrato.');
      } else {
        toast.success('Contrato eliminado');
      }
      fetchContracts();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error(parseApiError(error));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiado al portapapeles');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Add activity to schedule
  const addActivity = () => {
    if (!newActivity.activity.trim()) {
      toast.error('Ingrese el nombre de la actividad');
      return;
    }
    setFormData(prev => ({
      ...prev,
      schedule_activities: [...prev.schedule_activities, { ...newActivity }]
    }));
    setNewActivity({ activity: '', weeks: [] });
  };

  const removeActivity = (index) => {
    setFormData(prev => ({
      ...prev,
      schedule_activities: prev.schedule_activities.filter((_, i) => i !== index)
    }));
  };

  const toggleActivityWeek = (activityIndex, week) => {
    setFormData(prev => {
      const activities = [...prev.schedule_activities];
      const activity = activities[activityIndex];
      if (activity.weeks.includes(week)) {
        activity.weeks = activity.weeks.filter(w => w !== week);
      } else {
        activity.weeks = [...activity.weeks, week].sort((a, b) => a - b);
      }
      return { ...prev, schedule_activities: activities };
    });
  };

  // Add payment phase
  const addPaymentPhase = () => {
    if (!newPayment.description.trim() || !newPayment.amount) {
      toast.error('Complete los datos del pago');
      return;
    }
    setFormData(prev => ({
      ...prev,
      payment_phases: [...prev.payment_phases, { 
        ...newPayment, 
        amount: parseFloat(newPayment.amount),
        week: parseInt(newPayment.week)
      }]
    }));
    setNewPayment({ description: '', amount: '', week: 1 });
  };

  const removePaymentPhase = (index) => {
    setFormData(prev => ({
      ...prev,
      payment_phases: prev.payment_phases.filter((_, i) => i !== index)
    }));
  };

  // Add annual cost
  const addAnnualCost = () => {
    if (!newAnnualCost.activity.trim() || !newAnnualCost.value) {
      toast.error('Complete los datos del costo anual');
      return;
    }
    setFormData(prev => ({
      ...prev,
      annual_costs: [...prev.annual_costs, { 
        ...newAnnualCost, 
        value: parseFloat(newAnnualCost.value)
      }]
    }));
    setNewAnnualCost({ activity: '', value: '', time_period: 'Anual' });
  };

  const removeAnnualCost = (index) => {
    setFormData(prev => ({
      ...prev,
      annual_costs: prev.annual_costs.filter((_, i) => i !== index)
    }));
  };

  // Add module
  const addModule = () => {
    if (!newModule.trim()) return;
    setFormData(prev => ({
      ...prev,
      modules: [...prev.modules, newModule.trim()]
    }));
    setNewModule('');
  };

  const removeModule = (index) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <Edit3 className="w-4 h-4" /> Borrador
          </span>
        );
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            <Send className="w-4 h-4" /> Enviado
          </span>
        );
      case 'client_signed':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" /> Pendiente Firma Analytica
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" /> Completado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <FileText className="w-4 h-4" /> {status}
          </span>
        );
    }
  };

  const filteredContracts = contracts.filter(contract => {
    if (filter === 'all') return true;
    return contract.status === filter;
  });

  const stats = {
    total: contracts.length,
    draft: contracts.filter(c => c.status === 'draft').length,
    sent: contracts.filter(c => c.status === 'sent').length,
    client_signed: contracts.filter(c => c.status === 'client_signed').length,
    completed: contracts.filter(c => c.status === 'completed').length
  };

  const totalPayments = formData.payment_phases.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_3ee71ab0-856c-41a8-8e43-f382a70126f5/artifacts/3okyucn0_LOGO%20ANALYTICA.png" 
                alt="Analytica" 
                className="h-10"
              />
              <div>
                <h1 className="text-xl font-bold text-[#2F3D8E]">Sistema de Contratos</h1>
                <p className="text-sm text-gray-500">Gestión de contratos digitales</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Bienvenido, <strong>{user?.username}</strong>
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Kanban Module Menu (reemplaza los botones dispersos del header) */}
      <ModuleKanbanMenu
        user={user}
        active="contratos"
        onAction={{
          brochure: async () => {
            try { await generateBrochure(); toast.success('Brochure descargado'); }
            catch { toast.error('Error generando brochure'); }
          },
          fixLinks: (user?.username === 'Gerencia' || ['admin', 'gerencia'].includes(user?.role)) ? async () => {
            if (!window.confirm('Regenerar todos los enlaces de firma usando el dominio actual? Esto repara contratos enviados con dominios antiguos.')) return;
            try {
              const res = await axios.post(`${API}/contracts/fix-signing-links`);
              toast.success(`Listo: ${res.data.fixed} enlaces actualizados a ${res.data.base_url}`);
              fetchContracts();
            } catch (err) {
              toast.error('Error al regenerar enlaces');
            }
          } : null,
        }}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs switcher */}
        <div className="flex items-center gap-1 bg-white rounded-xl border p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveView('proposals')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'proposals' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-proposals"
          >
            Propuestas
          </button>
          <button
            onClick={() => setActiveView('contracts')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'contracts' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-contracts"
          >
            Contratos
          </button>
        </div>

        {activeView === 'proposals' && (
          <ProposalsTab onConverted={() => { setActiveView('contracts'); fetchContracts(); }} />
        )}

        {activeView === 'contracts' && (<>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
               onClick={() => setFilter('all')}>
            <div className="text-3xl font-bold text-[#2F3D8E]">{stats.total}</div>
            <div className="text-sm text-gray-500">Total</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
               onClick={() => setFilter('draft')}>
            <div className="text-3xl font-bold text-gray-600">{stats.draft}</div>
            <div className="text-sm text-gray-500">Borradores</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
               onClick={() => setFilter('sent')}>
            <div className="text-3xl font-bold text-blue-600">{stats.sent}</div>
            <div className="text-sm text-gray-500">Enviados</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
               onClick={() => setFilter('client_signed')}>
            <div className="text-3xl font-bold text-yellow-600">{stats.client_signed}</div>
            <div className="text-sm text-gray-500">Pendiente Firma</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
               onClick={() => setFilter('completed')}>
            <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-500">Completados</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchContracts} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            {filter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
                Ver todos
              </Button>
            )}
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#4C8C3B] hover:bg-[#3d7030]">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Contrato
          </Button>
        </div>

        {/* Contracts Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Contrato</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Cliente</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Software</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Valor</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Estado</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Fecha</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#2F3D8E]" />
                      <p className="mt-2 text-gray-500">Cargando contratos...</p>
                    </td>
                  </tr>
                ) : filteredContracts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No hay contratos {filter !== 'all' ? `con estado "${filter}"` : ''}
                    </td>
                  </tr>
                ) : (
                  filteredContracts.map((contract) => (
                    <tr key={contract.id} className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => { setSelectedContract(contract); setShowDetailModal(true); }}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#2F3D8E]">{contract.contract_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium">{contract.client_company_name || contract.client_name}</div>
                        <div className="text-sm text-gray-500">{contract.client_ruc}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div>{contract.software_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[#4C8C3B]">${contract.total_price?.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(contract.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(contract.created_at).toLocaleDateString('es-EC')}
                      </td>
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          {contract.status === 'draft' && (
                            <Button variant="ghost" size="sm" onClick={() => handleEditContract(contract)}
                                    title="Editar contrato" data-testid={`edit-contract-${contract.id}`}>
                              <Edit3 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(contract.id)}
                                  disabled={isDownloading} title="Descargar PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                          {contract.signing_link && (
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(contract.signing_link)}
                                    title="Copiar link de firma">
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContract(contract)}
                                  className="text-red-600 hover:bg-red-50"
                                  title={contract.status === 'draft' ? 'Eliminar borrador' : `Eliminar contrato (${contract.status})`}
                                  data-testid={`delete-contract-${contract.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </main>

      {/* Create Contract Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-[#2F3D8E]">{editingContractId ? 'Editar Contrato' : 'Nuevo Contrato'}</h2>
                <p className="text-sm text-gray-500">Paso {currentStep} de 4</p>
              </div>
              <button onClick={() => { setShowCreateModal(false); setEditingContractId(null); resetForm(); }}
                      className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                {['Cliente', 'Software', 'Cronograma', 'Pagos'].map((step, index) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${currentStep > index + 1 ? 'bg-green-500 text-white' : 
                        currentStep === index + 1 ? 'bg-[#2F3D8E] text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {currentStep > index + 1 ? '✓' : index + 1}
                    </div>
                    <span className={`ml-2 text-sm ${currentStep === index + 1 ? 'font-medium text-[#2F3D8E]' : 'text-gray-500'}`}>
                      {step}
                    </span>
                    {index < 3 && <div className="w-12 h-0.5 bg-gray-200 mx-2" />}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="p-6">
                {/* Step 1: Client Data */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-[#2F3D8E] flex items-center gap-2">
                      <Building2 className="w-5 h-5" /> Datos del Cliente
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label>Nombre de la Compañía *</Label>
                        <Input value={formData.client_company_name}
                               onChange={(e) => setFormData({...formData, client_company_name: e.target.value})}
                               placeholder="COMPAÑÍA EJEMPLO S.A." required />
                      </div>
                      
                      <div>
                        <Label>Título</Label>
                        <select value={formData.client_representative_title}
                                onChange={(e) => setFormData({...formData, client_representative_title: e.target.value})}
                                className="w-full px-3 py-2 border rounded-md">
                          <option value="Sr.">Sr.</option>
                          <option value="Sra.">Sra.</option>
                          <option value="Ing.">Ing.</option>
                          <option value="Dr.">Dr.</option>
                          <option value="Lic.">Lic.</option>
                          <option value="Arq.">Arq.</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label>Nombre del Representante Legal *</Label>
                        <Input value={formData.client_representative_name}
                               onChange={(e) => setFormData({...formData, client_representative_name: e.target.value})}
                               placeholder="Juan Pérez García" required />
                      </div>
                      
                      <div>
                        <Label>RUC / Identificación *</Label>
                        <Input value={formData.client_ruc}
                               onChange={(e) => setFormData({...formData, client_ruc: e.target.value})}
                               placeholder="1234567890001" required />
                      </div>
                      
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={formData.client_email}
                               onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                               placeholder="cliente@empresa.com" required />
                      </div>
                      
                      <div>
                        <Label>Teléfono *</Label>
                        <Input value={formData.client_phone}
                               onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                               placeholder="+593 99 999 9999" required />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label>Dirección *</Label>
                        <Input value={formData.client_address}
                               onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                               placeholder="Av. Principal 123, Edificio Centro" required />
                      </div>
                      
                      <div>
                        <Label>Ciudad *</Label>
                        <Input value={formData.client_city}
                               onChange={(e) => setFormData({...formData, client_city: e.target.value})}
                               placeholder="Quito" required />
                      </div>
                      
                      <div>
                        <Label>País</Label>
                        <Input value={formData.client_country}
                               onChange={(e) => setFormData({...formData, client_country: e.target.value})}
                               placeholder="Ecuador" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Software Data */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-[#2F3D8E] flex items-center gap-2">
                      <Briefcase className="w-5 h-5" /> Datos del Software
                    </h3>
                    
                    <div>
                      <Label>Nombre del Software *</Label>
                      <Input value={formData.software_name}
                             onChange={(e) => setFormData({...formData, software_name: e.target.value})}
                             placeholder="CRM, ERP, Sistema de Inventario, etc." required />
                    </div>
                    
                    <div>
                      <Label>Descripción (opcional)</Label>
                      <textarea value={formData.software_description}
                                onChange={(e) => setFormData({...formData, software_description: e.target.value})}
                                placeholder="Breve descripción del alcance del software..."
                                className="w-full px-3 py-2 border rounded-md h-24" />
                    </div>
                    
                    <div>
                      <Label>Módulos a Implementar</Label>
                      <div className="flex gap-2 mb-2">
                        <Input value={newModule}
                               onChange={(e) => setNewModule(e.target.value)}
                               placeholder="Ej: Facturación, Inventario, RRHH..."
                               onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addModule())} />
                        <Button type="button" onClick={addModule} variant="outline">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.modules.map((module, index) => (
                          <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                            {module}
                            <button type="button" onClick={() => removeModule(index)} className="hover:text-blue-900">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Schedule */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-[#2F3D8E] flex items-center gap-2">
                      <Calendar className="w-5 h-5" /> Cronograma de Implementación
                    </h3>
                    
                    <div>
                      <Label>Número Total de Semanas *</Label>
                      <Input type="number" min="1" max="52" value={formData.total_weeks}
                             onChange={(e) => setFormData({...formData, total_weeks: parseInt(e.target.value) || 1})}
                             className="w-32" />
                    </div>
                    
                    <div>
                      <Label>Agregar Actividad</Label>
                      <div className="flex gap-2 mb-2">
                        <Input value={newActivity.activity}
                               onChange={(e) => setNewActivity({...newActivity, activity: e.target.value})}
                               placeholder="Nombre de la actividad"
                               className="flex-1" />
                        <Button type="button" onClick={addActivity} variant="outline">
                          <Plus className="w-4 h-4 mr-1" /> Agregar
                        </Button>
                      </div>
                    </div>
                    
                    {formData.schedule_activities.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full border text-sm">
                          <thead className="bg-[#2F3D8E] text-white">
                            <tr>
                              <th className="px-2 py-2 text-left">Actividad</th>
                              {Array.from({ length: formData.total_weeks }, (_, i) => (
                                <th key={i} className="px-1 py-2 text-center">S{i + 1}</th>
                              ))}
                              <th className="px-2 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.schedule_activities.map((activity, actIndex) => (
                              <tr key={actIndex} className="border-b hover:bg-gray-50">
                                <td className="px-2 py-2">{activity.activity}</td>
                                {Array.from({ length: formData.total_weeks }, (_, weekIndex) => (
                                  <td key={weekIndex} className="px-1 py-2 text-center">
                                    <input type="checkbox"
                                           checked={activity.weeks.includes(weekIndex + 1)}
                                           onChange={() => toggleActivityWeek(actIndex, weekIndex + 1)}
                                           className="w-4 h-4" />
                                  </td>
                                ))}
                                <td className="px-2 py-2">
                                  <button type="button" onClick={() => removeActivity(actIndex)}
                                          className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 4: Payments */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <h3 className="font-semibold text-lg text-[#2F3D8E] flex items-center gap-2">
                      <Receipt className="w-5 h-5" /> Forma de Pago
                    </h3>
                    
                    {/* Payment Phases */}
                    <div className="p-4 bg-green-50 rounded-lg">
                      <Label className="text-green-800">Fases de Pago (Año 1)</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Input value={newPayment.description}
                               onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
                               placeholder="Descripción (ej: Anticipo)"
                               className="flex-1 min-w-[150px]" />
                        <Input type="number" step="0.01" value={newPayment.amount}
                               onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                               placeholder="Monto $"
                               className="w-32" />
                        <select value={newPayment.week}
                                onChange={(e) => setNewPayment({...newPayment, week: e.target.value})}
                                className="px-3 py-2 border rounded-md">
                          {Array.from({ length: formData.total_weeks }, (_, i) => (
                            <option key={i} value={i + 1}>Semana {i + 1}</option>
                          ))}
                        </select>
                        <Button type="button" onClick={addPaymentPhase} className="bg-green-600 hover:bg-green-700">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {formData.payment_phases.length > 0 && (
                        <table className="w-full mt-4 border text-sm">
                          <thead className="bg-[#4C8C3B] text-white">
                            <tr>
                              <th className="px-3 py-2 text-left">Descripción</th>
                              <th className="px-3 py-2 text-center">Semana</th>
                              <th className="px-3 py-2 text-right">Monto</th>
                              <th className="px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.payment_phases.map((phase, index) => (
                              <tr key={index} className="border-b">
                                <td className="px-3 py-2">{phase.description}</td>
                                <td className="px-3 py-2 text-center">S{phase.week}</td>
                                <td className="px-3 py-2 text-right font-semibold">${Number(phase.amount).toLocaleString()}</td>
                                <td className="px-3 py-2 text-center">
                                  <button type="button" onClick={() => removePaymentPhase(index)}
                                          className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-green-100 font-bold">
                              <td colSpan="2" className="px-3 py-2">TOTAL AÑO 1</td>
                              <td className="px-3 py-2 text-right text-green-700">${Number(totalPayments).toLocaleString()}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                    
                    {/* Annual Costs */}
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <Label className="text-blue-800">Costos Operativos Anuales (Desde Año 2)</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Input value={newAnnualCost.activity}
                               onChange={(e) => setNewAnnualCost({...newAnnualCost, activity: e.target.value})}
                               placeholder="Concepto (ej: Base de datos)"
                               className="flex-1 min-w-[150px]" />
                        <Input type="number" step="0.01" value={newAnnualCost.value}
                               onChange={(e) => setNewAnnualCost({...newAnnualCost, value: e.target.value})}
                               placeholder="Valor $"
                               className="w-32" />
                        <select value={newAnnualCost.time_period}
                                onChange={(e) => setNewAnnualCost({...newAnnualCost, time_period: e.target.value})}
                                className="px-3 py-2 border rounded-md">
                          <option value="Anual">Anual</option>
                          <option value="Mensual">Mensual</option>
                        </select>
                        <Button type="button" onClick={addAnnualCost} className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {formData.annual_costs.length > 0 && (
                        <table className="w-full mt-4 border text-sm">
                          <thead className="bg-[#5DA5D8] text-white">
                            <tr>
                              <th className="px-3 py-2 text-left">Concepto</th>
                              <th className="px-3 py-2 text-center">Periodo</th>
                              <th className="px-3 py-2 text-right">Valor</th>
                              <th className="px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.annual_costs.map((cost, index) => (
                              <tr key={index} className="border-b">
                                <td className="px-3 py-2">{cost.activity}</td>
                                <td className="px-3 py-2 text-center">{cost.time_period}</td>
                                <td className="px-3 py-2 text-right font-semibold">${Number(cost.value).toLocaleString()}</td>
                                <td className="px-3 py-2 text-center">
                                  <button type="button" onClick={() => removeAnnualCost(index)}
                                          className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Form Navigation */}
              <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between">
                <div>
                  {currentStep > 1 && (
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                      ← Anterior
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  {currentStep < 4 ? (
                    <Button type="button" onClick={goToNextStep}
                            className="bg-[#2F3D8E] hover:bg-[#243070]">
                      Siguiente →
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleCreateContract} className="bg-[#4C8C3B] hover:bg-[#3d7030]">
                      <Save className="w-4 h-4 mr-2" /> {editingContractId ? 'Guardar Cambios' : 'Guardar Borrador'}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contract Detail Modal */}
      {showDetailModal && selectedContract && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-[#2F3D8E]">{selectedContract.contract_number}</h2>
                {getStatusBadge(selectedContract.status)}
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <h3 className="font-semibold text-[#2F3D8E] mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Cliente
                  </h3>
                  <p className="font-medium">{selectedContract.client_company_name || selectedContract.client_name}</p>
                  <p className="text-sm text-gray-500">RUC: {selectedContract.client_ruc}</p>
                  <p className="text-sm text-gray-500">{selectedContract.client_address}</p>
                  <p className="text-sm text-gray-500">{selectedContract.client_city}, {selectedContract.client_country}</p>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <h3 className="font-semibold text-[#2F3D8E] mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Representante
                  </h3>
                  <p className="font-medium">{selectedContract.client_representative_title} {selectedContract.client_representative_name}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {selectedContract.client_email}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {selectedContract.client_phone}
                  </p>
                </div>
              </div>

              {/* Software Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-[#2F3D8E] mb-2">Software: {selectedContract.software_name}</h3>
                <p className="text-2xl font-bold text-[#4C8C3B]">${selectedContract.total_price?.toLocaleString()} USD</p>
                {selectedContract.modules?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedContract.modules.map((m, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">{m}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Signing Link */}
              {selectedContract.signing_link && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Link className="w-4 h-4" /> Link de Firma para el Cliente
                  </h3>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white rounded border text-sm truncate">
                      {selectedContract.signing_link}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(selectedContract.signing_link)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Signatures Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${selectedContract.client_signature ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <h4 className="font-medium flex items-center gap-2">
                    {selectedContract.client_signature ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                    Firma del Cliente
                  </h4>
                  {selectedContract.client_signed_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Firmado: {new Date(selectedContract.client_signed_at).toLocaleString('es-EC')}
                    </p>
                  )}
                </div>
                <div className={`p-4 rounded-lg ${selectedContract.analytica_signature || selectedContract.status === 'completed' ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <h4 className="font-medium flex items-center gap-2">
                    {selectedContract.analytica_signature || selectedContract.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                    Firma de Analytica
                  </h4>
                  {selectedContract.analytica_signed_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Firmado: {new Date(selectedContract.analytica_signed_at).toLocaleString('es-EC')}
                    </p>
                  )}
                </div>
              </div>

              {/* Certificate Info */}
              {selectedContract.certificate_number && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-800">Certificado de Autenticidad</h3>
                  <p className="font-mono text-sm">{selectedContract.certificate_number}</p>
                  {selectedContract.certificate_hash && (
                    <p className="text-xs text-gray-500 truncate">Hash: {selectedContract.certificate_hash}</p>
                  )}
                </div>
              )}

              {/* Invoice Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Receipt className="w-4 h-4" /> Factura
                </h3>
                {selectedContract.invoice_file ? (
                  <p className="text-green-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Factura adjuntada
                    <span className="text-xs text-gray-500">
                      ({new Date(selectedContract.invoice_uploaded_at).toLocaleDateString('es-EC')})
                    </span>
                  </p>
                ) : (
                  <div>
                    <input type="file" ref={invoiceInputRef} className="hidden" accept=".pdf,.png,.jpg,.jpeg"
                           onChange={(e) => handleUploadInvoice(e, selectedContract.id)} />
                    <Button variant="outline" size="sm" onClick={() => invoiceInputRef.current?.click()}
                            disabled={isUploading}>
                      {isUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Subir Factura
                    </Button>
                  </div>
                )}
              </div>

              {/* Custom Contract Template Section */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h3 className="font-semibold text-[#2F3D8E] mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Plantilla del Contrato
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  Por defecto se usa el contrato estandar de Analytica. Puede subir un <strong>.docx</strong> propio que
                  reemplazara el texto del contrato; Analytica conserva el encabezado, pie de pagina y bloque de firmas, y la
                  ortografia se corrige automaticamente.
                </p>
                {selectedContract.custom_contract_filename ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{selectedContract.custom_contract_filename}</span>
                      <span className="text-xs text-gray-500">
                        ({selectedContract.custom_contract_sections?.length || 0} secciones)
                      </span>
                      {selectedContract.custom_contract_spell_checked === false && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          ortografia no corregida
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input type="file" ref={customContractInputRef} className="hidden" accept=".docx"
                             onChange={(e) => handleUploadCustomContract(e, selectedContract.id)} />
                      <Button variant="outline" size="sm"
                              onClick={() => customContractInputRef.current?.click()}
                              disabled={isUploadingCustom || selectedContract.status === 'completed'}
                              data-testid="replace-custom-contract-btn">
                        {isUploadingCustom ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Reemplazar
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleRemoveCustomContract(selectedContract.id)}
                              disabled={selectedContract.status === 'completed'}
                              data-testid="remove-custom-contract-btn">
                        <Trash2 className="w-4 h-4 mr-2" /> Quitar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input type="file" ref={customContractInputRef} className="hidden" accept=".docx"
                           onChange={(e) => handleUploadCustomContract(e, selectedContract.id)} />
                    <Button variant="outline" size="sm"
                            onClick={() => customContractInputRef.current?.click()}
                            disabled={isUploadingCustom || selectedContract.status === 'completed'}
                            data-testid="upload-custom-contract-btn">
                      {isUploadingCustom ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Subir contrato personalizado (.docx)
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedContract.id)} disabled={isDownloading}>
                  <Download className="w-4 h-4 mr-2" /> PDF
                </Button>
                {selectedContract.status === 'draft' ? (
                  <>
                    <Button variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => handleEditContract(selectedContract)}
                            data-testid="edit-contract-detail-btn">
                      <Edit3 className="w-4 h-4 mr-2" /> Editar
                    </Button>
                    <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteContract(selectedContract)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteContract(selectedContract)}
                          data-testid="force-delete-contract-btn"
                          title="Eliminacion forzada (borra contrato sea cual sea su estado)">
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {selectedContract.status === 'draft' && (
                  <Button onClick={() => handleSendContract(selectedContract.id)}
                          className="bg-blue-600 hover:bg-blue-700">
                    <Send className="w-4 h-4 mr-2" /> Enviar al Cliente
                  </Button>
                )}
                {selectedContract.status === 'client_signed' && (
                  <Button onClick={() => setShowSignModal(true)}
                          className="bg-[#4C8C3B] hover:bg-[#3d7030]">
                    <PenTool className="w-4 h-4 mr-2" /> Firmar como Analytica
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytica Signature Modal */}
      {showSignModal && selectedContract && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#2F3D8E]">Firmar Contrato como Analytica</h2>
              <button onClick={() => { setShowSignModal(false); setUploadedSig(null); setCertData(null); setSignMode('draw'); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Importante:</strong> Al firmar este contrato, quedará oficialmente completado y se notificará al cliente. Esta acción no se puede deshacer.
                </p>
              </div>

              {/* Tabs: Dibujar / Subir Imagen / PAdES */}
              <div className="flex gap-1 border-b">
                <button onClick={() => setSignMode('draw')} type="button"
                        className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${signMode === 'draw' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        data-testid="contract-sign-mode-draw">
                  Dibujar
                </button>
                <button onClick={() => setSignMode('upload')} type="button"
                        className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${signMode === 'upload' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        data-testid="contract-sign-mode-upload">
                  Subir Imagen
                </button>
                <button onClick={() => setSignMode('cert')} type="button"
                        className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${signMode === 'cert' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        data-testid="contract-sign-mode-cert">
                  Firma con Certificado <span className="ml-1 text-[10px] bg-[#4C8C3B] text-white px-1.5 py-0.5 rounded-full">PAdES</span>
                </button>
              </div>

              {signMode === 'draw' && (
                <div>
                  <Label>Su Firma</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg mt-2 bg-white">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{ width: 400, height: 150, className: 'w-full' }}
                      backgroundColor="white"
                    />
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2" onClick={() => signatureRef.current?.clear()}>
                    Limpiar firma
                  </Button>
                </div>
              )}

              {signMode === 'upload' && (
                <div>
                  <Label>Subir imagen de la firma (PNG, JPG)</Label>
                  <input type="file" ref={sigFileRef} accept="image/*" className="hidden"
                         onChange={(e) => {
                           const f = e.target.files?.[0];
                           if (!f) return;
                           if (f.size > 5 * 1024 * 1024) { toast.error('Imagen excede 5MB'); return; }
                           const reader = new FileReader();
                           reader.onload = (ev) => setUploadedSig(ev.target.result);
                           reader.readAsDataURL(f);
                         }} data-testid="contract-sig-upload" />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg mt-2 p-6 text-center cursor-pointer hover:border-[#2F3D8E]"
                       onClick={() => sigFileRef.current?.click()}>
                    {uploadedSig ? (
                      <img src={uploadedSig} alt="firma" className="max-h-32 mx-auto rounded border" />
                    ) : (
                      <div className="text-gray-500 space-y-1">
                        <Upload className="w-8 h-8 mx-auto" />
                        <p className="text-sm">Haga clic para seleccionar imagen</p>
                        <p className="text-xs">PNG, JPG (max 5MB)</p>
                      </div>
                    )}
                  </div>
                  {uploadedSig && (
                    <Button variant="ghost" size="sm" className="mt-2" onClick={() => setUploadedSig(null)}>
                      Quitar imagen
                    </Button>
                  )}
                </div>
              )}

              {signMode === 'cert' && (
                <CertSignaturePanel
                  authToken={typeof window !== 'undefined' ? localStorage.getItem('token') : null}
                  onReady={(p12_base64, password, info) => setCertData({ p12_base64, password, info })}
                  onClear={() => setCertData(null)}
                />
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowSignModal(false); setUploadedSig(null); setCertData(null); setSignMode('draw'); }}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (signMode === 'draw') return handleSignByAnalytica();
                  if (signMode === 'upload') {
                    if (!uploadedSig) return toast.error('Suba la imagen primero');
                    return handleSignAnalyticaWithImage(uploadedSig);
                  }
                  if (signMode === 'cert') {
                    if (!certData) return toast.error('Cargue y valide su certificado primero');
                    return handleSignAnalyticaWithCert(certData.p12_base64, certData.password);
                  }
                }}
                disabled={isSigning}
                className="bg-[#4C8C3B] hover:bg-[#3d7030]"
                data-testid="contract-confirm-sign-btn">
                {isSigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenTool className="w-4 h-4 mr-2" />}
                Firmar y Completar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContratosPage;
